import { useEffect, useState } from "react"
import { ColorSwatchButton } from "../../ui/ColorPicker.tsx"
import type {
  FillStyle,
  IndicatorStyle,
  LevelStyle,
  LineStyleName,
  LineWidth,
  PlotStyle,
  PlotType
} from "../workspace/indicatorStyleSchema.ts"
import { PLOT_TYPES } from "../workspace/indicatorStyleSchema.ts"
import type { FillRow, LevelRow, PlotRow } from "./indicatorStyle.ts"

// TradingView's Style tab: one row per plot (visibility, colour, thickness, line
// style, plot type), then levels, fills, and the output options. Built from the
// indicator's metadata only, so no indicator needs code of its own.

const TYPE_NAMES: Readonly<Record<PlotType, string>> = {
  line: "Line",
  linebr: "Line with breaks",
  stepline: "Step line",
  steplinebr: "Step line with breaks",
  stepline_diamond: "Step line with diamonds",
  histogram: "Histogram",
  cross: "Cross",
  area: "Area",
  areabr: "Area with breaks",
  columns: "Columns",
  circles: "Circles"
}

const LINE_TYPES: ReadonlySet<PlotType> = new Set(["line", "linebr", "stepline", "steplinebr", "stepline_diamond"])

const setIn = <A,>(record: Record<string, A> | undefined, id: string, patch: Partial<A>): Record<string, A> => ({
  ...record,
  [id]: { ...record?.[id], ...patch } as A
})

export const withPlot = (style: IndicatorStyle, id: string, patch: Partial<PlotStyle>): IndicatorStyle =>
  ({ ...style, plots: setIn(style.plots, id, patch) })
const withLevel = (style: IndicatorStyle, id: string, patch: Partial<LevelStyle>): IndicatorStyle =>
  ({ ...style, levels: setIn(style.levels, id, patch) })
const withFill = (style: IndicatorStyle, id: string, patch: Partial<FillStyle>): IndicatorStyle =>
  ({ ...style, fills: setIn(style.fills, id, patch) })

const PRECISIONS = [0, 1, 2, 3, 4, 5, 6, 7, 8]

/**
 * A level's price, edited as text: an empty field or a lone "-" while typing
 * isn't a price (it would read as 0 and move the level), so only a number is passed on.
 */
function PriceField({ label, onPrice, price }: { readonly label: string; readonly price: number; readonly onPrice: (price: number) => void }) {
  const [text, setText] = useState(String(price))
  // Follows outside changes (Reset), not this field's own edits.
  useEffect(() => setText((current) => (Number(current) === price ? current : String(price))), [price])
  return (
    <input
      type="text"
      inputMode="decimal"
      aria-label={label}
      value={text}
      onChange={(event) => {
        setText(event.target.value)
        const value = event.target.value.trim()
        const parsed = Number(value)
        if (value !== "" && value !== "-" && Number.isFinite(parsed)) onPrice(parsed)
      }}
      onBlur={() => setText(String(price))}
    />
  )
}

export function IndicatorStyleTab({ fills, levels, onChange, plots, style }: {
  readonly plots: ReadonlyArray<PlotRow>
  readonly levels: ReadonlyArray<LevelRow>
  readonly fills: ReadonlyArray<FillRow>
  /** The draft: what differs from the defaults (rows give the defaults). */
  readonly style: IndicatorStyle
  readonly onChange: (style: IndicatorStyle) => void
}) {
  return (
    <div className="style-tab">
      {plots.map((row) => {
        const own = style.plots?.[row.id] ?? {}
        const type = own.type ?? row.type
        const color = own.color ?? row.color
        // Thickness and line style ride on the (first) swatch, as in TradingView.
        const lineControls = {
          ...(type === "columns" || type === "cross" || type === "circles"
            ? {}
            : { width: own.lineWidth ?? row.lineWidth, onWidth: (lineWidth: LineWidth) => onChange(withPlot(style, row.id, { lineWidth })) }),
          ...(LINE_TYPES.has(type)
            ? { lineStyle: own.lineStyle ?? row.lineStyle, onLineStyle: (lineStyle: LineStyleName) => onChange(withPlot(style, row.id, { lineStyle })) }
            : {})
        }
        return (
          <div key={row.id} className="style-row">
            <label className="style-check">
              <input
                type="checkbox"
                checked={own.visible !== false}
                onChange={(event) => onChange(withPlot(style, row.id, { visible: event.target.checked }))}
              />
              {row.title}
            </label>
            {row.palette
              ? (
                <span className="style-swatches">
                  {row.palette.map((base, index) => (
                    <ColorSwatchButton
                      key={index}
                      title={`${row.title} colour ${index + 1}`}
                      color={own.palette?.[String(index)] ?? base}
                      onColor={(picked) => onChange(withPlot(style, row.id, { palette: { ...own.palette, [String(index)]: picked } }))}
                      {...(index === 0 ? lineControls : {})}
                    />
                  ))}
                </span>
              )
              : (
                <ColorSwatchButton
                  title={`${row.title} colour`}
                  color={color}
                  onColor={(picked) => onChange(withPlot(style, row.id, { color: picked }))}
                  {...lineControls}
                />
              )}
            <select
              aria-label={`${row.title} plot type`}
              value={type}
              onChange={(event) => onChange(withPlot(style, row.id, { type: event.target.value as PlotType }))}
            >
              {PLOT_TYPES.map((t) => <option key={t} value={t}>{TYPE_NAMES[t]}</option>)}
            </select>
          </div>
        )
      })}

      {levels.length > 0 && <h3 className="style-heading">Levels</h3>}
      {levels.map((row) => {
        const own = style.levels?.[row.id] ?? {}
        return (
          <div key={row.id} className="style-row">
            <label className="style-check">
              <input
                type="checkbox"
                checked={own.visible !== false}
                onChange={(event) => onChange(withLevel(style, row.id, { visible: event.target.checked }))}
              />
              {row.title}
            </label>
            <ColorSwatchButton
              title={`${row.title} colour`}
              color={own.color ?? row.color}
              onColor={(color) => onChange(withLevel(style, row.id, { color }))}
              width={own.lineWidth ?? row.lineWidth}
              onWidth={(lineWidth) => onChange(withLevel(style, row.id, { lineWidth }))}
              lineStyle={own.lineStyle ?? row.lineStyle}
              onLineStyle={(lineStyle) => onChange(withLevel(style, row.id, { lineStyle }))}
            />
            <PriceField
              label={`${row.title} price`}
              price={own.price ?? row.price}
              onPrice={(price) => onChange(withLevel(style, row.id, { price }))}
            />
          </div>
        )
      })}

      {fills.length > 0 && <h3 className="style-heading">Fills</h3>}
      {fills.map((row) => {
        const own = style.fills?.[row.id] ?? {}
        return (
          <div key={row.id} className="style-row">
            <label className="style-check">
              <input
                type="checkbox"
                checked={own.visible !== false}
                onChange={(event) => onChange(withFill(style, row.id, { visible: event.target.checked }))}
              />
              {row.title}
            </label>
            {row.color !== null && (
              <ColorSwatchButton
                title={`${row.title} colour`}
                color={own.color ?? row.color}
                onColor={(color) => onChange(withFill(style, row.id, { color }))}
              />
            )}
          </div>
        )
      })}

      <h3 className="style-heading">Output values</h3>
      <div className="style-row">
        <label className="style-check" htmlFor="style-precision">Precision</label>
        <select
          id="style-precision"
          value={style.precision === undefined ? "" : String(style.precision)}
          onChange={(event) => {
            const { precision: _precision, ...rest } = style
            onChange(event.target.value === "" ? rest : { ...rest, precision: Number(event.target.value) })
          }}
        >
          <option value="">Default</option>
          {PRECISIONS.map((p) => <option key={p} value={p}>{p}</option>)}
        </select>
      </div>
      <label className="style-check">
        <input
          type="checkbox"
          checked={style.labelsOnPriceScale !== false}
          onChange={(event) => onChange({ ...style, labelsOnPriceScale: event.target.checked })}
        />
        Labels on price scale
      </label>
      <label className="style-check">
        <input
          type="checkbox"
          checked={style.valuesInStatusLine !== false}
          onChange={(event) => onChange({ ...style, valuesInStatusLine: event.target.checked })}
        />
        Values in status line
      </label>
    </div>
  )
}
